import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Phase 4D — Tenant detection (subdomain-aware).
 *
 * Resolves which tenant the visitor is on:
 *   1. ?tenant=<slug> query param        (preview/dev)
 *   2. /t/<slug>/...   path prefix        (preview/dev)
 *   3. Real subdomain (production)        — handled server-side via Host header
 *
 * The slug from (1) and (2) is forwarded to the backend so it can match the
 * preview environment too. In production, the backend reads the Host header
 * directly, so no query param is needed.
 */
const TenantContext = createContext(null);

function detectSlugFromUrl() {
  // 1) ?tenant=
  const params = new URLSearchParams(window.location.search);
  const q = (params.get("tenant") || "").trim().toLowerCase();
  if (q) return q;
  // 2) /t/<slug>/...
  const m = window.location.pathname.match(/^\/t\/([a-z0-9_-]+)(\/|$)/i);
  if (m) return m[1].toLowerCase();
  return "";
}

export function TenantProvider({ children }) {
  const [ctx, setCtx] = useState({ loading: true, mode: "platform", tenant: null, slug: "" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const slug = detectSlugFromUrl();
      try {
        const url = slug ? `/public/tenant-context?tenant=${encodeURIComponent(slug)}` : "/public/tenant-context";
        const { data } = await api.get(url);
        if (cancelled) return;
        setCtx({
          loading: false,
          mode: data.mode || "platform",
          tenant: data.tenant || null,
          slug,
          attempted: data.slug_attempted || "",
        });
        // Apply primary color globally (CSS var)
        const color = data?.tenant?.branding?.primary_color;
        if (color && /^#[0-9a-f]{3,8}$/i.test(color)) {
          document.documentElement.style.setProperty("--tenant-color", color);
        }
        // Update document title
        if (data?.tenant?.name) {
          document.title = `${data.tenant.name} · Transport-Plattform`;
        }
      } catch (_e) {
        if (!cancelled) setCtx({ loading: false, mode: "platform", tenant: null, slug });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <TenantContext.Provider value={ctx}>
      {children}
    </TenantContext.Provider>
  );
}

export const useTenant = () => useContext(TenantContext) || { loading: false, mode: "platform", tenant: null, slug: "" };
